import React, { useState } from 'react';
import StoreMap from './StoreMap.jsx';
import StoreCard from './StoreCard.jsx';
import AdvancedStoreSearch from './AdvancedStoreSearch.jsx';
import { useStoreContext } from '../contexts/StoreContext.jsx';

export function StoreLocator() {
  const [selected, setSelected] = useState(null);
  const { stores, config, isLoading, error } = useStoreContext();
  const [searchResults, setSearchResults] = useState(stores);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

  // Update search results when stores change
  React.useEffect(() => {
    setSearchResults(stores);
  }, [stores]);

  if (isLoading) {
    return (
      <section className="max-w-5xl mx-auto py-12 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading stores...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="max-w-5xl mx-auto py-12 px-4">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading stores: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-accent text-white px-4 py-2 rounded hover:bg-accent/80"
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto py-12 px-4">
      <div className="mb-8 text-center">
        <h2 className="text-4xl font-heading font-bold mb-4 text-primary">
          {config.storeLocator?.title || 'Find Stores'}
        </h2>
        <p className="text-text-secondary mb-6">
          Discover our bakeries and find the perfect location near you
        </p>
        
        {/* Search Toggle */}
        <div className="flex justify-center mb-6">
          <button
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            className="bg-accent text-white px-4 py-2 rounded-lg hover:bg-accent/90 transition-colors flex items-center gap-2"
          >
            🔍 {showAdvancedSearch ? 'Simple Search' : 'Advanced Search'}
          </button>
        </div>
      </div>

      {/* Search Interface */}
      <div className="mb-8">
        {showAdvancedSearch ? (
          <AdvancedStoreSearch
            onResults={setSearchResults}
            className="max-w-4xl mx-auto"
          />
        ) : (
          <div className="max-w-md mx-auto">
            <input
              type="text"
              placeholder={config.storeLocator?.searchPlaceholder || "Search stores..."}
              onChange={(e) => {
                const query = e.target.value;
                if (query.trim()) {
                  const filtered = stores.filter(store =>
                    store.name.toLowerCase().includes(query.toLowerCase()) ||
                    store.city.toLowerCase().includes(query.toLowerCase()) ||
                    store.address.toLowerCase().includes(query.toLowerCase())
                  );
                  setSearchResults(filtered);
                } else {
                  setSearchResults(stores);
                }
              }}
              className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
            />
          </div>
        )}
      </div>

      {/* Map */}
      <StoreMap 
        stores={searchResults} 
        selectedStore={selected} 
        onSelect={setSelected} 
        className="mb-8 h-96 rounded-xl overflow-hidden shadow-lg" 
      />

      {/* Results Summary */}
      <div className="mb-6 text-center">
        <p className="text-text-secondary">
          Found {searchResults.length} store{searchResults.length !== 1 ? 's' : ''}
          {searchResults.length !== stores.length && ` of ${stores.length} total`}
        </p>
      </div>

      {/* Store Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {searchResults.map((store) => (
          <StoreCard
            key={store.id}
            store={store}
            config={config}
            isSelected={selected === store.id}
            onSelect={setSelected}
          />
        ))}
      </div>

      {/* No Results */}
      {searchResults.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-medium text-primary mb-2">No stores found</h3>
          <p className="text-text-secondary mb-4">
            Try adjusting your search criteria or browse all available stores.
          </p>
          <button
            onClick={() => {
              setSearchResults(stores);
              setShowAdvancedSearch(false);
            }}
            className="bg-accent text-white px-4 py-2 rounded hover:bg-accent/90"
          >
            Show All Stores
          </button>
        </div>
      )}
    </section>
  );
}

export default StoreLocator;
